/**
 * PAGE ACCESS — one source for « may this member open this page? »
 * (2026-10-08, the « source unique » the structural pitfall asked for).
 *
 * Why this module exists: the sidebar and the preview « what does X see? » must
 * never disagree. They already shared the capability predicates; what was missing
 * was the layer that the explicit read DENY adds (`<family>.view` stored with
 * `granted = 0`, the « — » level of the page×level grid).
 *
 * THE RULE THIS FILE ENCODES: a page is hidden only for what the SERVER actually
 * refuses. Hiding more than the server would make a member lose a page he can
 * still use — the opposite error, and the worse one. So the mapping below lists
 * exactly the pages served by the four guarded mounts:
 *
 *   /api/bands/:id/rider       → content.rider      (config pages, musicians, crew,
 *                                                    the rider preview, the stage plot image)
 *   /api/bands/:id/setlists    → content.setlists
 *   /api/bands/:id/songs       → content.songbook
 *   /api/bands/:id/checklists  → content.checklists
 *
 *   (2026-10-13, lot 3a) the pages HORS contenu — venues, contacts, team,
 *   availability, aiAssistant — carried by their own mounts in server/index.js
 *   (`requireViewNotDenied('<page>', resolvePageBand)`), each with a `<page>.view`
 *   deny marker.
 *
 * NOT listed, on purpose: `stagePlot` (its editor reads `/api/stageplots`, a
 * separate store that no deny covers — hiding it would be a lie in the other
 * direction) and everything the deny does not touch (shows, tour, team, finance…).
 *
 * Corollary kept from the pitfall: hiding a link protects NOTHING. `server/index.js`
 * (the `requireViewNotDenied` middleware) is the guard; this file only keeps the UI
 * honest with it — nav item, preview AND the page guard all read `isPageDenied`.
 */
import {
  isFamilyDenied, canAnyFinance, canSeeRiderConfig,
  type AccessFamily, type WithDenied,
} from './permissions';
// Les prédicats de layout vivent dans leur module (source unique avec les 3 éditeurs).
import { canEditLayoutFor } from './layoutAccess';

/** Sidebar/preview page id → the family whose explicit deny closes it. */
export const PAGE_CONTENT_FAMILY: Readonly<Record<string, AccessFamily>> = {
  // content.rider — the whole surface served under /api/bands/:id/rider.
  configGeneral: 'content.rider',
  configHospitality: 'content.rider',
  configSound: 'content.rider',
  configIem: 'content.rider',
  configAudioSpecs: 'content.rider',
  configBackline: 'content.rider',
  configStageDesign: 'content.rider',
  configLights: 'content.rider',
  riderPreview: 'content.rider',
  musicians: 'content.rider',
  crew: 'content.rider',
  // The three other content families.
  setlists: 'content.setlists',
  songbook: 'content.songbook',
  checklists: 'content.checklists',
  // ── Pages HORS contenu (2026-10-13, lot 3a) ────────────────────────────────
  // Leur LECTURE est ouverte à tout membre et n'a pas de capacité délégable :
  // le refus « — » ne passe que par `<page>.view` (garde serveur
  // `requireViewNotDenied('<page>')` sur le montage correspondant). Le nom de la
  // famille EST l'id de page ici — chaque page garde SA garde serveur dans la
  // même passe (règle : ne cacher QUE ce que le serveur refuse).
  venues: 'venues',           // /api/venues (+ /api/venues/:id/files)
  contacts: 'contacts',       // /api/contacts
  team: 'team',               // /api/team (page People)
  availability: 'availability', // /api/bands/:id/availability
  aiAssistant: 'aiAssistant', // /api/ai (surfaces de lecture)
  // ── Family TOUR (2026-10-14, lot 3b) ───────────────────────────────────────
  // Itinerary / Tours / Advancing deviennent déniables : leur lecture est ouverte
  // à tout membre, leur garde serveur est `requireViewNotDenied('<page>',
  // resolvePageBand)` sur les montages `/api/itinerary`, `/api/tours` et
  // `/api/shows/:id/advancing`.
  //
  // ⚠️ `shows` est VOLONTAIREMENT ABSENT — ne PAS l'ajouter ici. C'est une
  // décision produit explicite (option B, 2026-10-14) : l'accueil de l'app EST la
  // liste des dates. Refuser « shows » n'aurait pas de sens, cela ouvrirait une
  // app vide (et la page reste servie par le montage `/api/shows`, non gardé).
  itinerary: 'itinerary',     // /api/itinerary/:bandId
  tours: 'tours',             // /api/tours (+ tourbook)
  advancing: 'advancing',     // /api/shows/:id/advancing
};

/** The families a member can be denied on — used by the tests and the docs. */
export const CONTENT_DENIABLE_PAGES: readonly string[] = Object.keys(PAGE_CONTENT_FAMILY);

/**
 * True when the member is EXPLICITLY denied the page — an owner/editor is never
 * denied (the server lets them read, `canWriteBand` short-circuits the middleware),
 * so callers pass the member's payload and this stays a pure lookup.
 */
export function isPageDenied(source: WithDenied | null | undefined, pageId: string): boolean {
  const family = PAGE_CONTENT_FAMILY[pageId];
  if (!family) return false;
  return isFamilyDenied(source, family);
}

/** Convenience: the deny state of the whole `content.rider` surface at once. */
export function isRiderContentDenied(source: WithDenied | null | undefined): boolean {
  return Object.values(PAGE_CONTENT_FAMILY).some((f) => f === 'content.rider' && isFamilyDenied(source, f));
}


/* ------------------------------------------------------------------ */
/*  LA SOURCE UNIQUE DE VISIBILITÉ (2026-10-08)                        */
/* ------------------------------------------------------------------ */

/**
 * L'état d'un membre, tel que le payload de band le porte.
 * C'est le SEUL objet dont la visibilité d'une page dépend.
 */
export interface PageAccessInput {
  /** Le compte démo partagé : il visite tout le produit (écriture gelée ailleurs). */
  isShowcase?: boolean;
  /** Administrateur plateforme : propriétaire implicite de chaque groupe. */
  isSuperAdmin?: boolean;
  /** Rôle sur le groupe ACTIF (`owner`|`editor`|`tech`|`crew`|`viewer`). */
  bandRole?: string | null;
  /** Capacités déléguées (voir `PERMISSIONS`). */
  permissions?: readonly string[] | null;
  /** Refus explicites de LECTURE (niveau « — », `<famille>.view` `granted = 0`). */
  denied?: string[] | null;
  /** Le lien au groupe existe (un membre sans lien ne voit rien). */
  linked?: boolean;
}

/** Les identifiants de page — les MÊMES que la sidebar et l'aperçu utilisent. */
export type PageId =
  | 'shows' | 'itinerary' | 'advancing' | 'tours' | 'venues' | 'contacts' | 'aiAssistant'
  | 'calendar'
  | 'bandSettings' | 'team' | 'availability' | 'musicians' | 'crew'
  | 'setlists' | 'songbook' | 'checklists' | 'payouts'
  | 'configGeneral' | 'configHospitality' | 'configSound' | 'configIem' | 'configAudioSpecs'
  | 'configBackline' | 'stagePlot' | 'configStageDesign' | 'configLights' | 'riderPreview'
  | 'riderLayout' | 'daysheetLayout' | 'tourBookLayout'
  | 'users' | 'apiKeys';

/** Un item de navigation : sa page, son libellé, son href. */
export interface PageDef {
  id: PageId;
  labelKey: string;
  /** Chemin relatif au groupe (la sidebar le préfixe par sa base) — l'aperçu ne s'en sert pas. */
  to?: string;
  /** Chemin GLOBAL (section Tour) — la sidebar ne le préfixe pas. */
  globalTo?: string;
  /** Sous-titre optionnel (pages People : « Personal data », « On-stage role »). */
  subKey?: string;
  /** Le lien n'est actif que sur sa route EXACTE (`end` de NavLink). */
  end?: boolean;
  /**
   * HORS MENU (2026-10-17) : la page reste gardée + accessible par URL, mais la
   * sidebar ET l'aperçu ne la listent pas (single source conservée : l'invariant
   * garde == nav == aperçu filtre les DEUX côtés sur ce même flag).
   */
  hidden?: boolean;
}

/** Une section de l'interface : son en-tête et ses entrées, dans l'ORDRE affiché. */
export interface PageGroupDef {
  id: string;
  labelKey: string;
  pages: PageDef[];
}

/**
 * LA structure (ordre + libellés + ROUTES) — la sidebar l'itère et la REND, l'aperçu
 * la LISTE. Un item ajouté ici apparaît des deux côtés : impossible d'en oublier un.
 * Les `to`/`globalTo` sont les chemins EXACTS historiquement servis par la sidebar
 * (les pages Tour sont montées en `/tour/…`, pas sous `/band/:slug`) — L1 a recopié
 * les href tels quels dans la table : une seule source, plus de lien codé en dur.
 */
export const PAGE_GROUPS: readonly PageGroupDef[] = [
  { id: 'tour', labelKey: 'sidebar.tour', pages: [
    { id: 'shows', labelKey: 'sidebar.shows', globalTo: '/tour/shows' },
    // Personal calendar (SPEC-PERSONAL-CALENDAR, 2026-10-21): a READING page
    // open to every member (viewer included — it is in VIEWER_SURFACE): the
    // user's own « concerned » shows + his subscription link.
    { id: 'calendar', labelKey: 'sidebar.calendar', globalTo: '/tour/calendar' },
    { id: 'itinerary', labelKey: 'sidebar.itinerary', globalTo: '/tour/itinerary' },
    { id: 'advancing', labelKey: 'sidebar.advancing', globalTo: '/tour/advancing' },
    { id: 'tours', labelKey: 'sidebar.tours', globalTo: '/tour/tours' },
    { id: 'venues', labelKey: 'sidebar.venues', globalTo: '/tour/venues' },
    { id: 'contacts', labelKey: 'sidebar.contacts', globalTo: '/tour/contacts' },
    { id: 'aiAssistant', labelKey: 'sidebar.aiAssistant', globalTo: '/ai-assistant' },
  ] },
  { id: 'band-info', labelKey: 'sidebar.bandInfo', pages: [
    { id: 'bandSettings', labelKey: 'sidebar.bandSettings', to: 'settings', end: true },
    // ⚠️ Availability HORS MENU (2026-10-17, décision user : mise de côté, optionnel) —
    // `hidden` la retire du MENU seulement : la page reste accessible par URL
    // (/band/:slug/availability), gardée dans PAGE_GROUPS (invariant garde == nav == aperçu),
    // données intactes. Réactiver = retirer le flag (même pattern que « Plans & Billing » v0.495).
    { id: 'availability', labelKey: 'sidebar.availability', to: 'availability', hidden: true },
  ] },
  { id: 'people-roles', labelKey: 'sidebar.peopleRoles', pages: [
    { id: 'team', labelKey: 'sidebar.team', globalTo: '/tour/team', subKey: 'sidebar.peopleSubtitle' },
    { id: 'musicians', labelKey: 'sidebar.musicians', to: 'musicians', subKey: 'sidebar.musiciansSubtitle' },
    { id: 'crew', labelKey: 'sidebar.crew', to: 'crew', subKey: 'sidebar.crewSubtitle' },
    { id: 'setlists', labelKey: 'sidebar.setlists', to: 'setlists' },
    { id: 'songbook', labelKey: 'sidebar.songbook', to: 'songbook' },
    { id: 'checklists', labelKey: 'sidebar.checklists', to: 'checklists' },
    { id: 'payouts', labelKey: 'sidebar.payouts', to: 'payouts' },
  ] },
  { id: 'rider-config', labelKey: 'sidebar.riderConfig', pages: [
    { id: 'configGeneral', labelKey: 'sidebar.configGeneral', to: 'config/general' },
    { id: 'configHospitality', labelKey: 'sidebar.configHospitality', to: 'config/hospitality' },
    { id: 'configSound', labelKey: 'sidebar.configSound', to: 'config/sound' },
    { id: 'configIem', labelKey: 'sidebar.configIem', to: 'config/iem' },
    { id: 'configAudioSpecs', labelKey: 'sidebar.configAudioSpecs', to: 'config/audio-specs' },
    { id: 'configBackline', labelKey: 'sidebar.configBackline', to: 'config/backline' },
    { id: 'stagePlot', labelKey: 'sidebar.stagePlot', to: 'stageplot-editor' },
    { id: 'configStageDesign', labelKey: 'sidebar.configStageDesign', to: 'config/stage-design' },
    { id: 'configLights', labelKey: 'sidebar.configLights', to: 'config/lights' },
  ] },
  // L1 (2026-10-12) — le rider a SON groupe, libellé NEUTRE : « Rider preview » vivait
  // dans « Rider Config », si bien qu'un `viewer` (9 pages de config cachées, le refus
  // explicite fermant le reste) voyait une section « RIDER CONFIG » ne contenant QUE
  // l'aperçu — une section fantôme qui mentait sur ce qu'il pouvait faire.
  { id: 'rider', labelKey: 'sidebar.rider', pages: [
    { id: 'riderPreview', labelKey: 'sidebar.riderPreview', to: 'rider', end: true },
  ] },
  { id: 'layout-editor', labelKey: 'sidebar.layoutEditor', pages: [
    { id: 'riderLayout', labelKey: 'sidebar.riderLayout', to: 'rider-layout' },
    { id: 'daysheetLayout', labelKey: 'sidebar.daysheetLayout', to: 'daysheet-layout' },
    { id: 'tourBookLayout', labelKey: 'sidebar.tourBookLayout', globalTo: '/tour/tourbook-layout' },
  ] },
  { id: 'admin', labelKey: 'sidebar.admin', pages: [
    { id: 'users', labelKey: 'sidebar.users', globalTo: '/users' },
    { id: 'apiKeys', labelKey: 'sidebar.apiKeys', globalTo: '/api-keys' },
  ] },
];

/* ------------------------------------------------------------------ */
/*  LA SURFACE VIEWER (2026-10-12)                                    */
/* ------------------------------------------------------------------ */

/**
 * LES PAGES D'UN `viewer` — la « surface viewer » déclarée (option B, décision
 * utilisateur 2026-10-12 ; **RÉDUITE le 2026-10-20, surface viewer v2** : Shows,
 * Itinerary, Advancing et Tours sortent de la surface — le viewer ne voit plus
 * QUE la **Route map** (Atlas, headerExtra) au menu Tour, et il ouvre un
 * événement en LECTURE (DaysheetViewer)). Il lit la page People, le rider et les
 * setlists/chansons. Il ne voit NI les fiches Musicians/Crew (détail technique,
 * redondant avec People), NI Venues/Contacts/AI assistant (outils de travail du
 * bureau — l'AI est désormais editor+, garde serveur /api/ai* comprise),
 * NI Availability, NI Band Settings (autorité).
 *
 * ⚠️ Ce n'est PAS un plafond : c'est un DÉFAUT, et la règle du projet
 * « le droit existe ⇒ l'entrée existe » s'applique PAR-DESSUS (cas Seb, cf.
 * `rbac-and-dual-role-2026.md`) — voir `VIEWER_CAPABILITY_PAGES`. Un viewer à qui
 * l'organisateur accorde `content.rider` DOIT voir les pages de config, sinon on
 * recrée le bug à l'envers (un membre privé de la page qu'il a le droit d'écrire).
 */
export const VIEWER_SURFACE: readonly PageId[] = [
  'team', 'riderPreview', 'setlists', 'songbook', 'checklists',
  // SPEC-PERSONAL-CALENDAR (2026-10-21): the personal calendar is a READING
  // page useful to every role — the viewer sees HIS OWN shows there.
  'calendar',
];

/**
 * Les pages qu'une CAPACITÉ réellement accordée rouvre à un `viewer` (le refus
 * explicite et l'absence de droit restent maîtres : `isPageVisible` n'accorde rien
 * ici, il se contente de NE PAS re-cacher ce que le prédicat de capacité a ouvert) :
 *   `content.rider` / `content.rider.*` → la section de config rider (et son
 *      stage plot, même store gated que le reste du rider) ;
 *   `layout.*`                          → l'éditeur de layout correspondant ;
 *   `finance.*`                         → Payouts ;
 *   `team.edit`                         → la page People.
 */
export const VIEWER_CAPABILITY_PAGES: readonly PageId[] = [
  'configGeneral', 'configHospitality', 'configSound', 'configIem', 'configAudioSpecs',
  'configBackline', 'configStageDesign', 'configLights', 'stagePlot',
  'riderLayout', 'daysheetLayout', 'tourBookLayout',
  'payouts', 'team',
];

/** La règle par page — la SEULE autorité. L'ordre des tests suit la spécification. */
function isPageVisible(input: PageAccessInput, page: PageId): boolean {
  const linked = input.linked !== false;
  const role = input.bandRole || null;
  const isOwner = role === 'owner' || !!input.isSuperAdmin;
  const isEditor = role === 'editor';
  const isMember = linked && !!role;
  // Les helpers historiques attendent un `permissions` MUTABLE : on copie.
  const asSource = {
    permissions: input.permissions ? [...input.permissions] : undefined,
    isSuperAdmin: !!input.isSuperAdmin,
    isShowcase: !!input.isShowcase,
    bandRole: role as import('../api').BandRole | null,
  };
  // ⚠️ `canSeeRiderConfig` est le SEUL bon prédicat ici : il couvre showcase, superadmin,
  // owner ET editor (le rôle), PLUS `content.rider` et les titulaires de SECTION. Un
  // simple `can('content.rider')` aurait caché les pages de config à un `editor` sans
  // aucun octroi — le bug Seb à l'envers, attrapé par le typechecker au moment du lot.
  const canRiderConfig = canSeeRiderConfig(asSource);
  const riderDenied = isRiderContentDenied(input);
  const allowed = ((): boolean => {
  switch (page) {
    // ── La vitrine : ouvert à tout membre (le serveur exige `viewer` pour lire) ──
    case 'shows': case 'itinerary': case 'advancing': case 'tours':
    // ── Le calendrier personnel (2026-10-21) : page de LECTURE des shows où le
    // membre est « concerné » + son lien d'abonnement .ics. Ouverte à tous les
    // rôles (le serveur ne renvoie que les shows du user lui-même), sans
    // capacité délégable et sans refus `<page>.view` : rien à cacher.
    case 'calendar':
    // venues/contacts/availability/team sont dans la vitrine ouverte à
    // tout membre, MAIS un refus explicite (`<page>.view`) les ferme (2026-10-13).
    case 'venues': case 'contacts': case 'team':
    case 'availability':
      return isMember && !isPageDenied(input, page);
    // ── L'AI assistant : RÉSERVÉ editor+ (2026-10-20, surface viewer v2) ───────
    // Décision produit : le copilote disparaît pour viewer ET tech/crew — les
    // crédits IA sont facturés et l'outil est celui du bureau. Côté serveur, les
    // routes /api/ai* refusent le reste (garde editor+ sur le montage). La démo
    // est incluse EXPRÈS (elle visite tout le produit, comme Band Settings), et
    // le refus explicite `aiAssistant.view` reste maître.
    case 'aiAssistant':
      return isMember && (isOwner || isEditor || !!input.isShowcase) && !isPageDenied(input, page);
    // ── L'autorité : jamais déléguée plus bas qu'editor ────────────────────────
    // `isShowcase` est inclus EXPRÈS : le compte démo partagé visite le produit, et la
    // sidebar historique lui montrait Band Settings (`canEditBand` incluait isShowcase).
    // L'écriture reste gelée ailleurs (blockShowcaseWrites), la vue ne l'est pas.
    case 'bandSettings': return isOwner || isEditor || !!input.isShowcase;
    case 'users': case 'apiKeys': return false;
    // ── La finance : propriétaire, ou une capacité de finance ──────────────────
    case 'payouts': return isMember && (isOwner || canAnyFinance({ permissions: input.permissions ? [...input.permissions] : undefined }));
    // ── Le rider : le droit complet OU une section, moins le refus explicite ───
    // (La SECTION de config n'est pas une page : sa visibilité se DÉDUIT de ses
    // enfants, via `groupVisible` — c'est ce qui garantit qu'un groupe vide disparaît.)
    case 'musicians': case 'crew': case 'riderPreview':
      return isMember && !isPageDenied(input, page);
    case 'configGeneral': case 'configHospitality': case 'configSound': case 'configIem':
    case 'configAudioSpecs': case 'configBackline': case 'configStageDesign': case 'configLights':
      // La section de CONFIG entière suit le droit rider (droit complet ou section) ;
      // le stageplot a son propre store (voir la note en tête de fichier).
      return isMember && canRiderConfig && !riderDenied;
    case 'stagePlot': return isMember && canRiderConfig && !riderDenied;
    // ── Un lien par capacité de layout (une seule suffit par page) ─────────────
    case 'riderLayout': return isMember && (isOwner || canEditLayoutFor(asSource, 'rider'));
    case 'daysheetLayout': return isMember && (isOwner || canEditLayoutFor(asSource, 'daysheet'));
    case 'tourBookLayout': return isMember && (isOwner || canEditLayoutFor(asSource, 'tourbook'));
    // ── Le contenu éditorial : ouvert en lecture, fermé par un refus explicite ──
    case 'setlists': case 'songbook': case 'checklists':
      return isMember && !isPageDenied(input, page);
    default: return false;
  }
  })();
  if (!allowed) return false;
  // ── LA SURFACE VIEWER (L1, 2026-10-12) ─────────────────────────────────────
  // Un `viewer` NE reçoit pas la vitrine ouverte à tout membre : son périmètre est
  // la surface déclarée, PLUS ce qu'une capacité réellement accordée a ouvert
  // (le test ci-dessus l'a déjà borné : ici on ne fait que NE PAS le re-cacher).
  // Le compte DÉMO est exclu : il visite tout le produit par conception.
  if (role === 'viewer' && !input.isShowcase) {
    return VIEWER_SURFACE.includes(page) || VIEWER_CAPABILITY_PAGES.includes(page);
  }
  return true;
}

/**
 * LA fonction : pour chaque page, « ce membre la voit-il ? ».
 * La sidebar la consomme, l'aperçu la consomme — ils ne peuvent plus diverger.
 */
export function pageVisibility(input: PageAccessInput): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const group of PAGE_GROUPS) for (const page of group.pages) out[page.id] = isPageVisible(input, page.id);
  return out;
}

/** Une section est visible dès qu'UNE de ses pages l'est. */
export function groupVisible(vis: Record<string, boolean>, group: PageGroupDef): boolean {
  // HORS MENU (`hidden`, 2026-10-17) : une page cachée ne peut pas rendre sa
  // section visible — la sidebar la saute AVANT le verdict de section.
  return group.pages.some((p) => !p.hidden && vis[p.id]);
}
